import React, { useState, useEffect } from 'react';
import { FaTimes, FaMapMarkerAlt, FaCircle } from 'react-icons/fa';
import { CloseModal } from '../sites/closemodal';

interface StateFormData {
    name: string;
}

interface AddStateOffcanvasProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: StateFormData) => void;
    loading?: boolean;
}

export const AddStateOffcanvas: React.FC<AddStateOffcanvasProps> = ({
    isOpen,
    onClose,
    onSubmit,
    loading = false
}) => {
    const [formData, setFormData] = useState<StateFormData>({
        name: ''
    });

    const [errors, setErrors] = useState<Partial<StateFormData>>({});
    const [touched, setTouched] = useState<Record<keyof StateFormData, boolean>>({
        name: false
    });
    const [showCloseModal, setShowCloseModal] = useState(false);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: ''
            });
            setErrors({});
            setTouched({
                name: false
            });
        } else {
            setShowCloseModal(false);
        }
    }, [isOpen]);

    // Validation
    const validateField = (name: keyof StateFormData, value: string) => {
        switch (name) {
            case 'name':
                if (!value.trim()) {
                    return 'State name is required';
                }
                if (value.trim().length < 2) {
                    return 'State name must be at least 2 characters long';
                }
                if (value.trim().length > 100) {
                    return 'State name must be less than 100 characters';
                }
                break;
        }
        return null;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const fieldName = name as keyof StateFormData;

        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));

        // Validate field if it has been touched
        if (touched[fieldName]) {
            const error = validateField(fieldName, value);
            setErrors(prev => ({
                ...prev,
                [fieldName]: error || undefined
            }));
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        const fieldName = name as keyof StateFormData;

        setTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));

        // Validate field
        const error = validateField(fieldName, formData[fieldName] || '');
        setErrors(prev => ({
            ...prev,
            [fieldName]: error || undefined
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<StateFormData> = {};
        let isValid = true;

        // Validate all fields
        (Object.keys(formData) as Array<keyof StateFormData>).forEach(field => {
            const error = validateField(field, formData[field] || '');
            if (error) {
                newErrors[field] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        setTouched({
            name: true
        });

        return isValid;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Prepare data for submission
        const submitData: StateFormData = {
            name: formData.name.trim()
        };

        onSubmit(submitData);
    };

    const requestClose = () => {
        if (!loading) {
            setShowCloseModal(true);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={requestClose} />

            {/* Offcanvas */}
            <div className="fixed right-0 top-0 h-full w-full sm:max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800">Add New State</h2>
                        <p className="text-sm text-gray-600 mt-1">Please fill out the form below to add a new state.</p>
                    </div>
                    <button
                        onClick={requestClose}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        disabled={loading}
                    >
                        <FaTimes className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="px-6 py-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* State Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    State Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaMapMarkerAlt className="w-4 h-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter state name"
                                        className={`block w-full pl-10 pr-3 py-3 border rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.name && touched.name
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300'
                                            }`}
                                        disabled={loading}
                                    />
                                </div>
                                {errors.name && touched.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">
                                    Enter the full name of the state (e.g., "Maharashtra", "Tamil Nadu")
                                </p>
                            </div>

                            {/* Info Box */}
                            <div className="bg-blue-50 border border-blue-200 rounded p-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <FaMapMarkerAlt className="w-4 h-4 text-blue-600" />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-medium text-blue-900 mb-1">State Information</h4>
                                        <ul className="text-xs text-blue-700 space-y-1">
                                            <li>• State name is required and must be unique</li>
                                            <li>• State names are case-insensitive for uniqueness</li>
                                            <li>• New states will be active by default</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={requestClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || !formData.name.trim()}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <FaMapMarkerAlt className="w-4 h-4" />
                                    Create State
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <CloseModal
                isOpen={showCloseModal}
                onClose={() => setShowCloseModal(false)}
                onConfirm={() => {
                    setShowCloseModal(false);
                    onClose();
                }}
            />
        </>
    );
};
